import { useEffect, useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $deleteUserAccountsStore } from '@lib/store';
import { cn, PaletteUtils } from '@shared/utils';
import type { GetAccountUsersResponse } from '@shared/api';
import { Button, Icon, Modal, Text } from '@shared/widgets';
import CloseIcon from '@assets/icons/close.svg';

interface RemoveManagerModalProps {
  isOpen: boolean;
  onClose: (confirm?: boolean) => void;
  accountId: number;
  data: GetAccountUsersResponse[];
}

export const RemoveManagerModal = (props: RemoveManagerModalProps) => {
  const deleteUserAccounts = useUnit($deleteUserAccountsStore.store);

  const [managersData, setManagersData] = useState<GetAccountUsersResponse[]>(props.data);
  const [selectedManagers, setSelectedManagers] = useState<string[]>([]);

  const { t } = useTranslation('account');

  useEffect(() => {
    setManagersData(props.data);
  }, [props.data]);

  const closeHandler = () => {
    setSelectedManagers([]);
    props.onClose();
  };
  const confirmRemoveHandler = async () => {
    if (!selectedManagers.length) {
      closeHandler();
      return;
    }
    await Promise.allSettled(
      selectedManagers.map((account) => $deleteUserAccountsStore.events.deleteUserAccounts({ userIds: account, accountIds: [props.accountId] })),
    );
    setSelectedManagers([]);
    props.onClose(true);
  };

  return (
    <Modal isOpen={props.isOpen} onClose={closeHandler} title={t('managers.modals.removeManager.title')}>
      <div className='my-3 flex flex-col gap-3'>
        {managersData.map((manager) => (
          <div
            key={manager.id}
            className={cn(
              'flex items-center justify-between gap-3 bg-ghost-white px-4 py-2 font-semibold dark:bg-soft-black',
              PaletteUtils.isVcfcPalette && 'dark:bg-light/5',
              selectedManagers.includes(manager.id) && 'opacity-50',
            )}
          >
            <div className='flex h-11 w-11 items-center justify-center rounded-full bg-primary'>
              <Text color={PaletteUtils.getColor('black', 'white')}>{manager.firstName[0]}</Text>
            </div>
            <Text className='truncate'>
              {manager.firstName} {manager.lastName}
            </Text>
            <Icon
              icon={<CloseIcon />}
              className={cn(
                'cursor-pointer transition-opacity duration-200',
                'hover:opacity-70',
                selectedManagers.includes(manager.id) && 'pointer-events-none cursor-default',
              )}
              onClick={() => setSelectedManagers((prev) => [...prev, manager.id])}
            />
          </div>
        ))}
      </div>
      <Button className='mt-8' color='primary' fullWidth disabled={deleteUserAccounts.loading} onClick={confirmRemoveHandler}>
        {t('buttons.confirm', { ns: 'common' })}
      </Button>
    </Modal>
  );
};
